import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../../../shared/components/Icon';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import FilterPopup from '../../../../shared/components/modals/FilterPopup';
import FunnelChart from './components/FunnelChart';
import SessionMetricsCards from './components/SessionMetricsCards';
import ActivityOverTimeChart from './components/ActivityOverTimeChart';
import TransactionMetricsCards from './components/TransactionMetricsCards';
import TransactionActivityChart from './components/TransactionActivityChart';
import axiosInstance from '../../../../shared/services/api/axiosInstance';
import { useOverviewFilters } from '../../../../shared/context/OverviewFilterContext';
import { getDefaultDateRange } from '../../../../shared/utils/dateUtils';

const Hub = () => {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const { appliedFilters, updateFilters, hasActiveFilters, teamId } = useOverviewFilters();
  
  // Data states
  const [funnelData, setFunnelData] = useState(null);
  const [sessionMetrics, setSessionMetrics] = useState(null);
  const [activityData, setActivityData] = useState([]);
  const [transactionMetrics, setTransactionMetrics] = useState(null);
  const [transactionActivityData, setTransactionActivityData] = useState([]);
  const [dappsData, setDappsData] = useState([]);
  const [deviceData, setDeviceData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [userActivityTimeRange, setUserActivityTimeRange] = useState('monthly');
  const [transactionActivityTimeRange, setTransactionActivityTimeRange] = useState('monthly');
  const [error, setError] = useState(null);
  const [hasInitializedFilters, setHasInitializedFilters] = useState(false);

  // Fetch all hub data
  // Fetch hub metrics (funnel + session) - only when filters or teamId change (NOT timeRange)
  const fetchHubMetrics = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      if (!teamId) {
        console.warn('No team ID available, skipping API call');
        return;
      }
      
      const params = {
        team_id: teamId
      };

      // Add website filter if selected
      if (appliedFilters.websites && appliedFilters.websites.length > 0) {
        params.websites = appliedFilters.websites.join(',');
      }

      // Add contract filter if selected
      if (appliedFilters.contracts && appliedFilters.contracts.length > 0) {
        params.contracts = appliedFilters.contracts.map(c => c.id || c).join(',');
      }

      // Add date range if specified
      if (appliedFilters.dateRange?.start) {
        params.from = appliedFilters.dateRange.start;
        params.to = appliedFilters.dateRange.end;
      }

      const hubResponse = await axiosInstance.get('/overview/hub-metrics', { params });

      if (hubResponse.data?.success) {
        setFunnelData(hubResponse.data.funnel);
        setSessionMetrics(hubResponse.data.sessionMetrics);
      } else {
        console.warn('Hub metrics response not successful:', hubResponse.data);
      }

    } catch (error) {
      console.error('Error fetching hub metrics:', error);
      setError(error.message);
      setFunnelData({ uniqueUsers: 0, web3Users: 0, walletsConnected: 0, transacted: 0 });
      setSessionMetrics({ totalSessions: 0, pagesPerVisit: 0, avgVisitDuration: 0, bounceRate: 0 });
    } finally {
      setIsLoading(false);
    }
  }, [appliedFilters, teamId]);

  // Fetch activity chart - when filters, teamId, or timeRange changes
  const fetchActivityChart = useCallback(async () => {
    try {
      if (!teamId) {
        setActivityData([]);
        return;
      }

      // Get website IDs for activity chart - use selected websites or all websites for team
      let websiteIds = [];
      
      try {
        const websitesResponse = await axiosInstance.get(`/website/team/${teamId}`);
        if (websitesResponse.data?.websites?.length > 0) {
          const allWebsiteIds = websitesResponse.data.websites.map(w => w.id);
          
          // Use selected websites from filters, or all websites if none selected
          if (appliedFilters.websites && appliedFilters.websites.length > 0) {
            // Convert site_id (UUID) to website_id (integer) by matching with fetched websites
            websiteIds = appliedFilters.websites.map(selectedId => {
              const website = websitesResponse.data.websites.find(w => 
                w.site_id === selectedId || w.id === parseInt(selectedId, 10)
              );
              return website ? website.id : null;
            }).filter(id => id !== null);
            console.log(`Using selected websites: ${websiteIds.join(', ')}`);
          } else {
            // No specific websites selected, use all websites for the team
            websiteIds = allWebsiteIds;
            console.log(`No websites selected, using all websites for team: ${websiteIds.join(', ')}`);
          }
        }
      } catch (error) {
        console.warn('Failed to fetch websites for team:', error);
      }
      
      // Only fetch activity chart if we have valid website IDs
      if (websiteIds && websiteIds.length > 0) {
        const activityResponse = await axiosInstance.get('/user-journey/activity-chart-multiple', { 
          params: { 
            team_id: teamId,
            website_ids: websiteIds.join(','),
            timeRange: userActivityTimeRange
          } 
        });

        if (activityResponse?.data?.data) {
          setActivityData(activityResponse.data.data);
        } else {
          setActivityData([]);
        }
      } else {
        setActivityData([]);
      }

    } catch (error) {
      console.error('Error fetching activity chart:', error);
      setActivityData([]);
    }
  }, [appliedFilters, teamId, userActivityTimeRange]);

  // Fetch transaction metrics - when filters or teamId change (NOT timeRange)
  const fetchTransactionMetrics = useCallback(async () => {
    try {
      if (!teamId) {
        setTransactionMetrics({ transactedWallets: 0, transactionCount: 0, transactionValue: 0 });
        return;
      }
      
      const params = {
        team_id: teamId
      };

      // Add contract filter if selected
      if (appliedFilters.contracts && appliedFilters.contracts.length > 0) {
        params.contracts = appliedFilters.contracts.map(c => c.id || c).join(',');
      }

      // Add date range if specified
      if (appliedFilters.dateRange?.start) {
        params.from = appliedFilters.dateRange.start;
        params.to = appliedFilters.dateRange.end;
      }

      const transactionResponse = await axiosInstance.get('/overview/transaction-metrics', { params });

      if (transactionResponse.data?.success) {
        setTransactionMetrics(transactionResponse.data.transactionMetrics);
      } else {
        console.warn('Transaction metrics response not successful:', transactionResponse.data);
        setTransactionMetrics({ transactedWallets: 0, transactionCount: 0, transactionValue: 0 });
      }

    } catch (error) {
      console.error('Error fetching transaction metrics:', error);
      setTransactionMetrics({ transactedWallets: 0, transactionCount: 0, transactionValue: 0 });
    }
  }, [appliedFilters, teamId]);

  // Fetch transaction activity chart - when filters, teamId, or timeRange changes
  const fetchTransactionActivityChart = useCallback(async () => {
    try {
      if (!teamId) {
        setTransactionActivityData([]);
        return;
      }

      const params = {
        team_id: teamId,
        timeRange: transactionActivityTimeRange
      };

      // Add contract filter if selected
      if (appliedFilters.contracts && appliedFilters.contracts.length > 0) {
        params.contracts = appliedFilters.contracts.map(c => c.id || c).join(',');
      }

      // Add date range if specified
      if (appliedFilters.dateRange?.start) {
        params.from = appliedFilters.dateRange.start;
        params.to = appliedFilters.dateRange.end;
      }

      const transactionActivityResponse = await axiosInstance.get('/overview/transaction-activity-chart', { params });

      if (transactionActivityResponse?.data?.data) {
        setTransactionActivityData(transactionActivityResponse.data.data);
      } else {
        setTransactionActivityData([]);
      }

    } catch (error) {
      console.error('Error fetching transaction activity chart:', error);
      setTransactionActivityData([]);
    }
  }, [appliedFilters, teamId, transactionActivityTimeRange]);

  // Fetch user journey metrics (dapps and device data) - when filters or teamId change
  const fetchUserJourneyMetrics = useCallback(async () => {
    try {
      if (!teamId) {
        setDappsData([]);
        setDeviceData([]);
        return;
      }

      // Get website ID for user journey metrics (use first selected website or first available)
      let websiteId = null;
      
      try {
        const websitesResponse = await axiosInstance.get(`/website/team/${teamId}`);
        if (websitesResponse.data?.websites?.length > 0) {
          const allWebsiteIds = websitesResponse.data.websites.map(w => w.id);
          
          // Use first selected website from filters, or first available website
          if (appliedFilters.websites && appliedFilters.websites.length > 0) {
            const selectedId = appliedFilters.websites[0];
            const website = websitesResponse.data.websites.find(w => 
              w.site_id === selectedId || w.id === parseInt(selectedId, 10)
            );
            websiteId = website ? website.id : allWebsiteIds[0];
          } else {
            websiteId = allWebsiteIds[0];
          }
        }
      } catch (error) {
        console.warn('Failed to fetch websites for user journey metrics:', error);
      }

      // Only fetch if we have a valid website ID
      if (websiteId) {
        const params = {
          team_id: teamId,
          website_id: websiteId
        };

        // Add date range if specified
        if (appliedFilters.dateRange?.start) {
          params.from = appliedFilters.dateRange.start;
          params.to = appliedFilters.dateRange.end;
        }

        const metricsResponse = await axiosInstance.get('/user-journey/metrics', { params });

        if (metricsResponse?.data) {
          setDappsData(metricsResponse.data.dappsInteracted || []);
          setDeviceData(metricsResponse.data.deviceDistribution || []);
        } else {
          setDappsData([]);
          setDeviceData([]);
        }
      } else {
        setDappsData([]);
        setDeviceData([]);
      }

    } catch (error) {
      console.error('Error fetching user journey metrics:', error);
      setDappsData([]);
      setDeviceData([]);
    }
  }, [appliedFilters, teamId]);

  // Initialize default filters (first website and first contract) when Hub mounts
  useEffect(() => {
    const initializeDefaultFilters = async () => {
      if (!teamId || hasInitializedFilters) return;
      
      // Only initialize if no filters are currently set
      const hasWebsites = appliedFilters.websites && appliedFilters.websites.length > 0;
      const hasContracts = appliedFilters.contracts && appliedFilters.contracts.length > 0;
      
      if (hasWebsites || hasContracts) {
        setHasInitializedFilters(true);
        return;
      }

      try {
        // Fetch websites
        let firstWebsiteId = null;
        try {
          const isNumericId = typeof teamId === 'number' || (!isNaN(teamId) && Number.isInteger(Number(teamId)));
          const websitesResponse = isNumericId
            ? await axiosInstance.get(`/website/team/${teamId}`)
            : await axiosInstance.get(`/website/name/${encodeURIComponent(teamId)}`);
          
          if (websitesResponse.data?.websites?.length > 0) {
            const activeWebsites = websitesResponse.data.websites.filter(w => w.is_verified || w.isVerified);
            if (activeWebsites.length > 0) {
              firstWebsiteId = activeWebsites[0].site_id || activeWebsites[0].siteId;
            }
          }
        } catch (err) {
          console.warn('Error fetching websites for default filter:', err);
        }

        // Fetch contracts
        let firstContract = null;
        try {
          let teamName = teamId;
          if (typeof teamId === 'number' || !isNaN(teamId)) {
            teamName = localStorage.getItem('selectedTeam') || teamId;
          }
          
          const contractsResponse = await axiosInstance.get(`/smart-contracts/team/${encodeURIComponent(teamName)}`);
          
          if (contractsResponse.data?.contracts?.length > 0) {
            const contract = contractsResponse.data.contracts[0];
            firstContract = {
              id: contract.contractId || contract.id,
              address: contract.address || contract.contract_address,
              name: contract.name || contract.contract_name || `Contract ${(contract.address || contract.contract_address || '').substring(0, 6)}...${(contract.address || contract.contract_address || '').substring((contract.address || contract.contract_address || '').length - 4)}`,
              blockchain: contract.blockchain || contract.chain,
              tokenSymbol: contract.tokenSymbol || contract.symbol || contract.contract_symbol,
              contractType: contract.contractType || 'main'
            };
          }
        } catch (err) {
          console.warn('Error fetching contracts for default filter:', err);
        }

        // Update filters with first website and first contract
        if (firstWebsiteId || firstContract) {
          updateFilters({
            websites: firstWebsiteId ? [firstWebsiteId] : [],
            contracts: firstContract ? [firstContract] : [],
            sessions: appliedFilters.sessions || [],
            userIdentities: appliedFilters.userIdentities || [],
            dateRange: appliedFilters.dateRange || getDefaultDateRange()
          });
        }
        setHasInitializedFilters(true);
      } catch (error) {
        console.error('Error initializing default filters:', error);
        setHasInitializedFilters(true); // Set to true even on error to prevent infinite loops
      }
    };

    initializeDefaultFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId, hasInitializedFilters]);

  // Reset initialization flag when team changes
  useEffect(() => {
    setHasInitializedFilters(false);
  }, [teamId]);

  // Fetch hub metrics when filters or teamId change (NOT timeRange)
  useEffect(() => {
    if (teamId) {
      fetchHubMetrics();
      fetchTransactionMetrics();
      fetchUserJourneyMetrics();
    }
  }, [fetchHubMetrics, fetchTransactionMetrics, fetchUserJourneyMetrics, teamId]);

  // Fetch activity chart when filters, teamId, or timeRange changes
  useEffect(() => {
    if (teamId) {
      fetchActivityChart();
      fetchTransactionActivityChart();
    }
  }, [fetchActivityChart, fetchTransactionActivityChart, teamId]);

  const handleFiltersChange = (newFilters) => {
    updateFilters(newFilters);
  };

  const styles = {
    primaryBg: "#ffffff",
    secondaryBg: "#DDE2EE",
    textPrimary: "#0f172a",
    textSecondary: "#666666",
    accentColor: "#1d0c46",
    borderColor: "#DDE2EE"
  };

  return (
    <div className="relative h-full w-full overflow-y-auto">
      {/* Filter Button - Vertical, fixed to right edge */}
      <button
        onClick={() => setIsFilterOpen(true)}
        className="fixed z-10 transition-colors shadow-sm flex items-center justify-center gap-2 bg-white/80 backdrop-blur-sm hover:bg-white text-[#475569]"
        style={{ 
          fontFamily: 'Raleway, sans-serif',
          right: 0,
          top: '16px',
          padding: '16px 8px',
          writingMode: 'vertical-rl',
          textOrientation: 'mixed',
          transform: 'none',
          border: '1px solid #DDE2EE',
          borderRight: 'none',
          borderRadius: 0
        }}
      >
        <Icon name="filter" size={18} style={{ transform: 'rotate(90deg)' }} />
        <span className="text-sm font-medium">Filters</span>
        {hasActiveFilters() && (
          <span className="text-xs opacity-90">•</span>
        )}
      </button>

      {/* Page Content */}
      <div className="p-4 md:p-6 lg:p-8 space-y-6 pr-20">
        {/* Error Message */}
        {error && (
          <div className="p-4 text-center" style={{ 
            backgroundColor: styles.primaryBg, 
            border: `1px solid ${styles.borderColor}`,
            color: styles.textSecondary,
            fontFamily: 'Raleway, sans-serif'
          }}>
            Error loading data: {error}
          </div>
        )}

        {/* Funnel Chart */}
        <div className="p-4 md:p-6" style={{ backgroundColor: styles.primaryBg, border: `1px solid ${styles.borderColor}` }}>
          <h3 className="text-sm font-medium mb-4" style={{ color: styles.accentColor, fontFamily: 'Raleway, sans-serif' }}>
            User Conversion Funnel
          </h3>
          <FunnelChart data={funnelData} isLoading={isLoading} />
        </div>

        {/* Session Metrics Cards */}
        <SessionMetricsCards metrics={sessionMetrics} isLoading={isLoading} />

        {/* User Activity Over Time Chart */}
        <div className="p-4 md:p-6" style={{ backgroundColor: styles.primaryBg, border: `1px solid ${styles.borderColor}` }}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium" style={{ color: styles.accentColor, fontFamily: 'Raleway, sans-serif' }}>
              User Activity Over Time
            </h3>
            {/* Time Range Toggle - Match User Journey style */}
            <div className="flex border overflow-hidden" style={{ borderColor: styles.borderColor }}>
              {['daily', 'weekly', 'monthly', 'yearly'].map((range) => (
                <button
                  key={range}
                  onClick={() => setUserActivityTimeRange(range)}
                  className={`px-3 py-1 text-xs transition-colors ${
                    userActivityTimeRange === range
                      ? 'text-white'
                      : 'bg-white hover:bg-gray-50'
                  }`}
                  style={{
                    fontFamily: 'Raleway, sans-serif',
                    backgroundColor: userActivityTimeRange === range ? styles.accentColor : styles.primaryBg,
                    color: userActivityTimeRange === range ? '#ffffff' : styles.textPrimary
                  }}
                >
                  {range.charAt(0).toUpperCase() + range.slice(1)}
                </button>
              ))}
            </div>
          </div>
          <div className="h-80">
            <ActivityOverTimeChart data={activityData} timeRange={userActivityTimeRange} isLoading={isLoading} />
          </div>
        </div>

        {/* DApps and Device Type Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* List of DApps Interacted */}
          <div className="p-4 md:p-6" style={{ backgroundColor: styles.primaryBg, border: `1px solid ${styles.borderColor}` }}>
            <div className="mb-4">
              <h3 className="text-sm font-medium" style={{ color: styles.accentColor, fontFamily: 'Raleway, sans-serif' }}>
                List of DApps Interacted
              </h3>
            </div>
            {isLoading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5, 6, 7].map((i) => (
                  <div key={i} className="h-8 animate-pulse" style={{ backgroundColor: styles.secondaryBg }}></div>
                ))}
              </div>
            ) : (() => {
              if (!dappsData || dappsData.length === 0) {
                return (
                  <div className="text-center py-8 text-xs" style={{ color: styles.textSecondary }}>
                    No DApps data available
                  </div>
                );
              }
              
              // Data is already sorted by backend (ORDER BY user_count DESC), just take top 7
              const displayData = dappsData.slice(0, 7);
              
              return (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr style={{ borderBottom: `1px solid ${styles.borderColor}` }}>
                        <th className="text-left py-3 px-4 text-xs font-medium" style={{ color: styles.accentColor, fontFamily: 'Raleway, sans-serif' }}>
                          Name of Dapp
                        </th>
                        <th className="text-right py-3 px-4 text-xs font-medium" style={{ color: styles.accentColor, fontFamily: 'Raleway, sans-serif' }}>
                          Count
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayData.map((item, index) => (
                        <tr 
                          key={`${item.label}-${index}`}
                          style={{ 
                            borderBottom: index < displayData.length - 1 ? `1px solid ${styles.borderColor}` : 'none'
                          }}
                        >
                          <td className="py-3 px-4 text-xs" style={{ color: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }}>
                            {item.label}
                          </td>
                          <td className="py-3 px-4 text-xs text-right" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
                            {item.value?.toLocaleString() || 0}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })()}
          </div>

          {/* Device Type Split */}
          <div className="p-4 md:p-6" style={{ backgroundColor: styles.primaryBg, border: `1px solid ${styles.borderColor}` }}>
            <h3 className="text-sm font-medium mb-4 text-center" style={{ color: styles.accentColor, fontFamily: 'Raleway, sans-serif' }}>
              Device Type Split
            </h3>
            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="w-64 h-64 animate-pulse rounded-full" style={{ backgroundColor: styles.secondaryBg }}></div>
              </div>
            ) : (() => {
              if (!deviceData || deviceData.length === 0) {
                return (
                  <div className="text-center py-8 text-xs" style={{ color: styles.textSecondary }}>
                    No device data available
                  </div>
                );
              }

              // Donut chart colors - Shades of #1d0c46
              const donutColors = ['#1d0c46', '#3d2d66', '#5d4d86'];

              // Format data for PieChart (convert percentage to number for display)
              const pieChartData = deviceData.map(item => ({
                name: item.label,
                value: item.value,
                percentage: item.value
              }));

              return (
                <div className="flex flex-col lg:flex-row items-center justify-center gap-6">
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={pieChartData}
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        innerRadius={50}
                        fill="#8884d8"
                        dataKey="value"
                        nameKey="name"
                      >
                        {pieChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={donutColors[index % donutColors.length]} />
                        ))}
                      </Pie>
                      <Tooltip 
                        formatter={(value, name) => {
                          const item = pieChartData.find(d => d.name === name);
                          return [`${value.toFixed(1)}%`, name];
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="space-y-2">
                    {pieChartData.map((item, index) => {
                      const segmentColor = donutColors[index % donutColors.length];
                      return (
                        <div key={index} className="flex items-center gap-2">
                          <div
                            className="w-4 h-4 rounded"
                            style={{ backgroundColor: segmentColor }}
                          />
                          <span className="text-xs" style={{ color: segmentColor, fontFamily: 'Raleway, sans-serif' }}>
                            {item.name}: {item.percentage.toFixed(1)}%
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
          </div>
        </div>

        {/* Transaction Metrics Cards */}
        <TransactionMetricsCards metrics={transactionMetrics} isLoading={isLoading} />

        {/* Transaction Activity Over Time Chart */}
        <div className="p-4 md:p-6" style={{ backgroundColor: styles.primaryBg, border: `1px solid ${styles.borderColor}` }}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium" style={{ color: styles.accentColor, fontFamily: 'Raleway, sans-serif' }}>
              Transaction Activity Over Time
            </h3>
            {/* Time Range Toggle - Match User Journey style */}
            <div className="flex border overflow-hidden" style={{ borderColor: styles.borderColor }}>
              {['daily', 'weekly', 'monthly', 'yearly'].map((range) => (
                <button
                  key={range}
                  onClick={() => setTransactionActivityTimeRange(range)}
                  className={`px-3 py-1 text-xs transition-colors ${
                    transactionActivityTimeRange === range
                      ? 'text-white'
                      : 'bg-white hover:bg-gray-50'
                  }`}
                  style={{
                    fontFamily: 'Raleway, sans-serif',
                    backgroundColor: transactionActivityTimeRange === range ? styles.accentColor : styles.primaryBg,
                    color: transactionActivityTimeRange === range ? '#ffffff' : styles.textPrimary
                  }}
                >
                  {range.charAt(0).toUpperCase() + range.slice(1)}
                </button>
              ))}
            </div>
          </div>
          <div className="h-80">
            <TransactionActivityChart data={transactionActivityData} timeRange={transactionActivityTimeRange} isLoading={isLoading} />
          </div>
        </div>
      </div>

      {/* Filter Popup */}
      <FilterPopup 
        isOpen={isFilterOpen} 
        onClose={() => setIsFilterOpen(false)}
        onFiltersChange={handleFiltersChange}
        appliedFilters={appliedFilters}
        teamId={localStorage.getItem('selectedTeam') || 'Cryptique'}
      />
    </div>
  );
};

export default Hub;

